import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import categories from '../../types/categories';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import './Browse.css';
import { Item } from '../../types/Item';

interface ListedItem {
  id: number;
  Item: Item;
  quantity: number;
  price: number;
  expiresMasked: string;
}

interface ItemGroup {
  itemTypeId: number;
  representative: ListedItem; // lowest-priced listing
  others: ListedItem[];       // remaining listings sorted by price asc
}

interface BrowseProps {
  category: keyof typeof categories | null;
  subcategory: string | null;
  searchTerm: string;
}

const getItemsPerPage = () => {
  if (typeof window !== 'undefined' && window.innerHeight < 1000) return 14;
  return 20;
};

const EXPIRES_BADGE: Record<string, { cls: string }> = {
  '< 6h':  { cls: 'expires-urgent'  },
  '< 12h': { cls: 'expires-soon'    },
  '< 24h': { cls: 'expires-normal'  },
  '> 24h': { cls: 'expires-plenty'  },
};

const Browse: React.FC<BrowseProps> = ({ category, subcategory, searchTerm }) => {
  const [groupsPerPage] = useState(getItemsPerPage);
  const [listedItems, setListedItems] = useState<ListedItem[]>([]);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: number]: number }>({});
  const [buyingId, setBuyingId] = useState<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const { mercenary } = useMercenary();

  useEffect(() => {
    setCurrentPage(1);
    setExpandedGroups(new Set());
  }, [category, subcategory, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedGroups(new Set());
    fetchListedItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory, sortField, sortOrder, searchTerm]);

  const fetchListedItems = async () => {
    try {
      // Fetch all listings at once, pagination is handled client-side over groups
      const response = await axios.get(`/api/auction/items/browse`, {
        params: {
          category,
          subcategory,
          sortField,
          sortOrder,
          page: 1,
          limit: 9999,
          search: searchTerm,
        },
      });
      const items: ListedItem[] = response.data.items;
      setListedItems(items);
      setSelectedQuantities(
        Object.fromEntries(items.map((item: ListedItem) => [item.id, 1]))
      );
    } catch (error) {
      displayToast('Error fetching listed items', 'error');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleBuy = async (itemId: number) => {
    const quantity = selectedQuantities[itemId] || 1;
    setBuyingId(itemId);
    try {
      const response = await axios.post(`/api/auction/buy`, { itemId, mercenaryId: mercenary?.id, quantity });
      if (response.data.success) {
        displayToast('Purchase successful!', 'success');
        fetchListedItems();
      } else {
        displayToast('Purchase failed: ' + (response.data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        displayToast('Purchase failed: ' + (error.response?.data.error || 'Server error'), 'error');
      } else {
        displayToast('Purchase failed: Unknown error', 'error');
      }
    } finally {
      setBuyingId(null);
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleQuantityChange = (itemId: number, value: string) => {
    if (value === "" || /^\d+$/.test(value)) {
      setSelectedQuantities((prev) => ({
        ...prev,
        [itemId]: value === "" ? 0 : Math.min(Number(value), listedItems.find((item) => item.id === itemId)?.quantity || 1),
      }));
    }
  };

  const handleQuantityBlur = (itemId: number) => {
    const maxQuantity = listedItems.find((item) => item.id === itemId)?.quantity || 1;
    const currentQuantity = selectedQuantities[itemId] || ""; 
    setSelectedQuantities((prev) => ({
      ...prev,
      [itemId]: currentQuantity === "" ? 1 : Math.max(1, Math.min(Number(currentQuantity), maxQuantity)),
    }));
  };

  const EQUIPPABLE_CATEGORIES = ['Weapon', 'Shield', 'Armor', 'Accessory'];

  // Group listings by item type for stackable items; equippable items are never grouped (each is a unique instance)
  const allItemGroups = useMemo<ItemGroup[]>(() => {
    const groupMap = new Map<number, ListedItem[]>();
    const equippableListings: ListedItem[] = [];

    for (const listing of listedItems) {
      if (EQUIPPABLE_CATEGORIES.includes(listing.Item.itemCategory)) {
        equippableListings.push(listing);
      } else {
        const tid = listing.Item.id;
        if (!groupMap.has(tid)) groupMap.set(tid, []);
        groupMap.get(tid)!.push(listing);
      }
    }

    const groups: ItemGroup[] = [];

    // Each equippable listing is its own standalone row
    for (const listing of equippableListings) {
      groups.push({ itemTypeId: listing.id, representative: listing, others: [] });
    }

    // Stack stackable items as before
    groupMap.forEach((listings) => {
      const sorted = [...listings].sort((a, b) => a.price - b.price);
      groups.push({
        itemTypeId: sorted[0].Item.id,
        representative: sorted[0],
        others: sorted.slice(1),
      });
    });
    // Sort groups by the representative's field to match server sort intent
    groups.sort((a, b) => {
      let av: string | number, bv: string | number;
      if (sortField === 'price') {
        av = a.representative.price; bv = b.representative.price;
      } else if (sortField === 'quantity') {
        av = a.representative.quantity; bv = b.representative.quantity;
      } else {
        av = a.representative.Item.name.toLowerCase();
        bv = b.representative.Item.name.toLowerCase();
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return groups;
  }, [listedItems, sortField, sortOrder]);

  // Client-side pagination over groups
  const totalGroups = allItemGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalGroups / groupsPerPage));
  const itemGroups = useMemo(() => {
    const start = (currentPage - 1) * groupsPerPage;
    return allItemGroups.slice(start, start + groupsPerPage);
  }, [allItemGroups, currentPage, groupsPerPage]);

  const toggleGroup = (itemTypeId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(itemTypeId)) next.delete(itemTypeId);
      else next.add(itemTypeId);
      return next;
    });
  };

  const getSortIcon = (field: string) => {
    if (sortField === field) {
      return sortOrder === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  return (
    <div className="browse-container">
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* ── Table Header ── */}
      <div className="browse-header">
        <span className="browse-col col-name sortable" onClick={() => handleSort('name')}>
          Item{getSortIcon('name')}
        </span>
        <span className="browse-col col-expires sortable" onClick={() => handleSort('expiresMasked')}>
          Expires{getSortIcon('expiresMasked')}
        </span>
        <span className="browse-col col-qty sortable" onClick={() => handleSort('quantity')}>
          Qty{getSortIcon('quantity')}
        </span>
        <span className="browse-col col-price sortable" onClick={() => handleSort('price')}>
          Price{getSortIcon('price')}
        </span>
        <span className="browse-col col-action">Action</span>
      </div>

      {/* ── Item List ── */}
      <div className="browse-list">
        {itemGroups.length > 0 ? (
          itemGroups.map((group, idx) => {
            const { representative: item, others, itemTypeId } = group;
            const isExpanded = expandedGroups.has(itemTypeId);
            const hasMore = others.length > 0;
            const expBadge = EXPIRES_BADGE[item.expiresMasked] || { cls: 'expires-normal' };
            const totalCost = item.price * (selectedQuantities[item.id] || 1);
            const isEquippable = EQUIPPABLE_CATEGORIES.includes(item.Item.itemCategory);
            return (
              <div key={`${isEquippable ? 'inst' : 'type'}-${itemTypeId}`} className="browse-row-group">
                {/* Main / representative row */}
                <div
                  className={`browse-row ${idx % 2 === 0 ? 'even' : 'odd'}${hasMore ? ' expandable' : ''}`}
                  onClick={hasMore ? () => toggleGroup(itemTypeId) : undefined}
                >
                  <div className="browse-col col-name">
                    <div className="browse-item-cell">
                      <span className={`browse-expand-chevron${isExpanded ? ' expanded' : ''}${!hasMore ? ' invisible' : ''}`}>
                        ▶
                      </span>
                      <Tooltip itemId={item.Item.id}>
                        <img
                          className="browse-item-icon"
                          src={getItemImageSrc(item.Item)}
                          alt={item.Item.name}
                          onError={handleImgError}
                        />
                        <span className="browse-item-name">{item.Item.name}</span>
                      </Tooltip>
                      {hasMore && (
                        <span className="browse-more-badge">+{others.length} more</span>
                      )}
                    </div>
                  </div>
                  <div className="browse-col col-expires">
                    <span className={`browse-expires-badge ${expBadge.cls}`}>
                      {item.expiresMasked}
                    </span>
                  </div>
                  <div className="browse-col col-qty" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      className="browse-qty-input"
                      value={selectedQuantities[item.id] || ""}
                      min={1}
                      max={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      onBlur={() => handleQuantityBlur(item.id)}
                      placeholder="1"
                    />
                    <span className="browse-qty-total">/ {item.quantity}</span>
                  </div>
                  <div className="browse-col col-price">
                    <span className="browse-price-value">{totalCost.toLocaleString()}</span>
                    <span className="browse-price-unit">silver</span>
                  </div>
                  <div className="browse-col col-action" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="browse-buy-btn"
                      onClick={() => handleBuy(item.id)}
                      disabled={buyingId === item.id}
                    >
                      {buyingId === item.id ? '...' : 'Buy'}
                    </button>
                  </div>
                </div>

                {/* Expanded sub-rows */}
                {isExpanded && others.map((subItem) => {
                  const subBadge = EXPIRES_BADGE[subItem.expiresMasked] || { cls: 'expires-normal' };
                  const subCost = subItem.price * (selectedQuantities[subItem.id] || 1);
                  return (
                    <div key={subItem.id} className="browse-row browse-subrow">
                      <div className="browse-col col-name">
                        <div className="browse-item-cell browse-subrow-indent">
                          <span className="browse-subrow-line" />
                          <span className="browse-item-name browse-subrow-name">{subItem.Item.name}</span>
                        </div>
                      </div>
                      <div className="browse-col col-expires">
                        <span className={`browse-expires-badge ${subBadge.cls}`}>
                          {subItem.expiresMasked}
                        </span>
                      </div>
                      <div className="browse-col col-qty">
                        <input
                          type="number"
                          className="browse-qty-input"
                          value={selectedQuantities[subItem.id] || ""}
                          min={1}
                          max={subItem.quantity}
                          onChange={(e) => handleQuantityChange(subItem.id, e.target.value)}
                          onBlur={() => handleQuantityBlur(subItem.id)}
                          placeholder="1"
                        />
                        <span className="browse-qty-total">/ {subItem.quantity}</span>
                      </div>
                      <div className="browse-col col-price">
                        <span className="browse-price-value">{subCost.toLocaleString()}</span>
                        <span className="browse-price-unit">silver</span>
                      </div>
                      <div className="browse-col col-action">
                        <button
                          className="browse-buy-btn"
                          onClick={() => handleBuy(subItem.id)}
                          disabled={buyingId === subItem.id}
                        >
                          {buyingId === subItem.id ? '...' : 'Buy'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div className="browse-empty">
            <p>No items available{category ? ` in ${category}` : ''}.</p>
            <p className="browse-empty-hint">Try browsing a different category or adjusting your filters.</p>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalGroups > 0 && (
        <div className="browse-pagination">
          <button 
            onClick={() => handlePageChange(1)}
            className="browse-page-btn"
            disabled={currentPage === 1}
          >
            ««
          </button>
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            className="browse-page-btn"
            disabled={currentPage === 1}
          >
            ‹ Prev
          </button>
          <span className="browse-page-info">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            className="browse-page-btn"
            disabled={currentPage === totalPages}
          >
            Next ›
          </button>
          <button 
            onClick={() => handlePageChange(totalPages)}
            className="browse-page-btn"
            disabled={currentPage === totalPages}
          >
            »»
          </button>
        </div>
      )}
    </div>
  );
};

export default Browse;
